/**
 * `/search` — TASK 9.4.
 *
 * The mock's `_search(V,q)` view model (L1044-1065) + template (L344-384),
 * wired to `GET /api/search` (TASK 4.2). What changed and why:
 *
 *   mock in-memory filter over `db.*` -> ONE grouped, ranked endpoint call.
 *       Each group's `count` is the server's TOTAL across all pages (AC 11.1),
 *       so the header numbers no longer depend on how many rows are rendered.
 *   mock `sqTok` token + `_sTimer` -> the timer stays (it is the AC 11.2
 *       debounce) but the response-ordering half is NOT hand-rolled here:
 *       `api.search()` runs through the api-client's shared latest-wins guard
 *       (api-client.ts#createLatestWins) and reports a superseded response as
 *       `{stale:true}`. The `if (outcome.stale) return;` below IS the AC 11.8
 *       latest-query-wins discard — a slow "beat" can never overwrite the
 *       rendered "beatles", because the guard sequences EVERY search call and
 *       marks all but the newest one stale even when the abort loses the race.
 *   mock chips from `db.genres` -> an INJECTED `genres` list (the client holds
 *       no genre table; the composition root supplies one). The view enforces
 *       the contract shape either way: ≤ 8 chips, never "Unknown" (§3 /search
 *       populated state).
 *
 * ── DEBOUNCE (AC 11.2) — A REAL ONE ───────────────────────────────────────
 * Every keystroke CLEARS the pending timer and arms a fresh 250 ms one, so a
 * burst of typing issues exactly ONE request, carrying the final term. This is
 * deliberately a debounce and not a throttle: a throttle would fire mid-burst
 * with a half-typed term (and a prior story shipped exactly that bug under a
 * comment claiming otherwise). The tests in test/search-view.test.ts assert
 * the re-arm explicitly — a fixed timer from the first keystroke fails them.
 *
 * ── WHAT `epoch` IS (AND IS NOT) ──────────────────────────────────────────
 * Ordering between competing searches is the api-client's job (see above) —
 * this view owns no AbortController and no per-request generation. `epoch`
 * exists for the one case the shared guard cannot see: the user CLEARS the box
 * (or drops below the 2-char minimum) while a request is in flight. No newer
 * search supersedes that request, so its outcome comes back `{stale:false}` —
 * yet painting it would overwrite the restored pre-search view (AC 11.2/11.4).
 * Bumping `epoch` on clear/below-min marks every unsettled response unwanted.
 *
 * ── SERVER CONTRACT NOTES THAT SHAPED THIS VIEW ───────────────────────────
 *   * `q` bounds are measured AFTER trimming (dto.ts): under 2 chars is a 422,
 *     not an empty result — so the view never fires below the minimum and the
 *     idle helper, not "No results", is what a 1-char term shows. Over 100
 *     chars is REJECTED, not clamped, so the view refuses to fire there too
 *     ({@link SEARCH_TOO_LONG_HINT} — an honest hint beats a Retry button that
 *     re-sends a request the server has promised to refuse).
 *   * Every response group is `.optional()` and absence is MEANINGFUL:
 *     `genres` is OMITTED entirely while a genre filter is active (§10.6), so
 *     the chip-active render simply never sees the key — nothing is filtered
 *     client-side.
 *   * Search group `genres` items are bare `CatalogRef`s (id + name): the DTO
 *     carries NO album count, so the mock's "N albums" trailing text has no
 *     honest source and genre rows render a "GENRE" kind label instead
 *     (matching the ARTIST/ALBUM row treatment).
 *   * Search excludes `available:false` TRACKS by construction (AC 3.9), so a
 *     gone track in a songs group is a SERVER contract violation — the
 *     availability cascade runs with `surface:'search'` and reports through
 *     the injected diagnostics `warn` (see AvailabilityBadge.js). A gone
 *     ALBUM, by contrast, is the AC 3.9 retained exception and carries its
 *     marker here like everywhere else.
 *
 * ── TELEMETRY (W17) ───────────────────────────────────────────────────────
 * Every debounced search response that PAINTS emits `search_performed` with
 * `query_length` + `result_count` ONLY — never the query text (§11 PII note).
 * The emit goes through the injected emitter seam (state-store.js), never by
 * importing lib/telemetry-client.ts into a view; `POST /api/telemetry` does
 * not exist until TASK 11.1, and the emitter owns that concern.
 *
 * @module
 */

import {
  ERROR_CODES,
  SEARCH_QUERY_MAX_CHARS,
  SEARCH_QUERY_MIN_CHARS,
  TESTIDS,
  albumCardLink,
  artistRowLink,
  genreFilterChip,
  genreRowLink,
  playTrackButton,
} from '@streamymusic/shared';

import { getTelemetryEmitter } from '../../app/state-store.js';

import {
  AVAILABILITY_COPY,
  AvailabilityBadge,
  availabilityOpacity,
  availabilityState,
} from '../../components/AvailabilityBadge.js';
import { EmptyBlock, ErrorBlock } from '../../components/StateBlocks.js';
import { Chip, Spinner } from '../../components/primitives/index.js';
import { append, el } from '../../components/primitives/dom.js';
import { formatSeconds } from '../../components/primitives/format.js';
import { albumThumb, artistThumb } from '../../components/CoverThumb.js';
import { createSearchHistory } from '../../../lib/search-history.js';

/** AC 11.2 — the floor is "≥ 250 ms"; this is exactly that floor. */
export const SEARCH_DEBOUNCE_MS = 250;

/** mock L1066-1069 — every group renders its first 8 rows; `count` says the rest. */
export const SEARCH_GROUP_LIMIT = 8;

/** §3 /search populated state — "up to 8 chips". */
export const MAX_GENRE_CHIPS = 8;

/** mock L360 / ui-contract §9 — the idle / below-minimum helper. */
export const SEARCH_IDLE_HINT =
  'Type at least 2 characters to search the whole wall — artists, albums, songs and genres.';

/**
 * The over-maximum helper. Not a contract-crawled state (the mock's in-memory
 * search had no upper bound); it exists because the server REJECTS > 100 chars
 * rather than clamping (dto.ts), and firing anyway would put a Retry button on
 * an error that retrying can never fix.
 */
export const SEARCH_TOO_LONG_HINT = `Search terms max out at ${SEARCH_QUERY_MAX_CHARS} characters — trim it down to search.`;

/** mock L362 / ui-contract §10.6 — the retriable-error copy. */
export const SEARCH_ERROR_MESSAGE = 'Search fell over.';

/** mock L363 — the no-results headline. */
export const SEARCH_NO_RESULTS_HEADLINE = 'NADA.';

/**
 * mock L1064 — «No results for “<query>”[ in <Genre>].» Curly quotes are the
 * mock's own (“/”).
 *
 * @param {string} term the trimmed term that was searched
 * @param {string|null} genreName the active chip's name, if any
 * @returns {string}
 */
export function searchNoResultsMessage(term, genreName) {
  return `No results for “${term}”${genreName === null ? '' : ` in ${genreName}`}.`;
}

/**
 * mock L1067 — "artist · year", with NO year segment when the album has none
 * (the same explicit rule as AlbumDetail.js#albumMetaLine, AC 4.2).
 *
 * @param {import('@streamymusic/shared').AlbumCard} album
 * @returns {string}
 */
export function albumResultSubtitle(album) {
  return typeof album.year === 'number' && Number.isFinite(album.year)
    ? `${album.artistName} · ${album.year}`
    : album.artistName;
}

/**
 * mock L1068 — "artist — album".
 *
 * @param {import('@streamymusic/shared').SearchTrack} track
 * @returns {string}
 */
export function songResultSubtitle(track) {
  return `${track.artistName} — ${track.albumTitle}`;
}

/**
 * AC 11.1 group order + the mock's per-group heading accents (L367-379).
 * `key` is the response key, so the render loop and the DTO cannot drift.
 */
const GROUP_ORDER = /** @type {const} */ ([
  { key: 'artists', label: 'Artists', accent: 'sm-section-title--pink' },
  { key: 'albums', label: 'Albums', accent: 'sm-section-title--yellow' },
  { key: 'songs', label: 'Songs', accent: 'sm-section-title--teal' },
  { key: 'genres', label: 'Genres', accent: 'sm-section-title--purple' },
]);

/**
 * Builds the `/search` section. The skeleton (box, chip row, results host) is
 * built ONCE; only the results host's children swap between idle / in-flight /
 * error / no-results / grouped results.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'search'>,
 *   heading: HTMLElement,
 *   onPlayTrack: (track: import('@streamymusic/shared').SearchTrack) => void,
 *   genres?: readonly import('@streamymusic/shared').CatalogRef[],
 *   onBlocked?: (message: string, context: { state: string, trackId: string }) => void,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   downloadedTracks?: readonly string[],
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 *   telemetry?: import('../../app/state-store.js').TelemetryEmitter,
 *   debounceMs?: number,
 *   initialQuery?: string,
 *   initialGenreId?: string,
 *   onQueryChange?: (query: string, genreId: string|null) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` (router.js
 *   `PageHeading`) — handed in, never made here (shell contract: exactly one
 *   per route). `genres` feeds the AC 11.9 filter chips; the view caps them at
 *   {@link MAX_GENRE_CHIPS} and drops "Unknown" per §3. `onPlayTrack` is the
 *   song-row tap (AC 11.1: song results PLAY, they do not navigate);
 *   `onBlocked` receives the §10.6 banner copy when a marked row is tapped.
 *   `announce` is the shell's persistent polite live region (LiveRegions.js);
 *   `warn` is the app's diagnostics seam (see the module header). `telemetry`
 *   overrides the store-installed emitter (tests); when absent the emitter is
 *   read from the store AT EMIT TIME, so one installed after this view mounts
 *   still receives events. `initialQuery` seeds the box from the route's
 *   `?q=` and, when valid, searches immediately — a load, not a keystroke, so
 *   it does not wait out the debounce.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function SearchView(options) {
  const {
    api,
    heading,
    onPlayTrack,
    genres = [],
    onBlocked = () => {},
    /*
     * PRODUCT W3 — accepted HERE so wiring them in `modules/app/index.js` is a
     * one-liner rather than a signature change.
     *
     * `FavoritesView`, `ArtistDetail` and `AlbumDetail` all take these; this
     * view did not, so when the composition root eventually passed them it
     * would have silently dropped them here and marked every search result
     * "not downloaded" for an offline user — a failure with no error and no
     * type check to catch it, because the options object is untyped at the
     * call site. Defaulting `offline` to `false` keeps today's behaviour
     * exactly (no marker) until the root passes a value.
     */
    offline = false,
    downloadedAlbums = [],
    downloadedTracks = [],
    announce,
    warn = (message) => {
      console.warn(message);
    },
    telemetry,
    debounceMs = SEARCH_DEBOUNCE_MS,
    initialQuery = '',
    initialGenreId,
    onQueryChange,
    history = createSearchHistory(),
  } = options;

  const downloadedAlbumIds = new Set(downloadedAlbums);
  const downloadedTrackIds = new Set(downloadedTracks);

  const section = el('section', { attrs: { 'data-screen-label': 'Search' } });

  /* ── the search box (mock L347-352) ─────────────────────────────────── */

  const input = /** @type {HTMLInputElement} */ (
    el('input', {
      className: 'sm-search__input',
      testId: TESTIDS.SEARCH_INPUT,
      attrs: {
        id: 'sm-search',
        type: 'search',
        placeholder: 'Artists, albums, songs, genres…',
      },
      on: { input: () => onInput() },
    })
  );

  const clearButton = el('button', {
    className: 'sm-btn sm-btn--icon sm-btn--icon-18 sm-btn--icon-muted',
    text: '✕',
    testId: TESTIDS.CLEAR_SEARCH_BUTTON,
    // Hidden, not absent: the contract shows the ✕ "only when query present",
    // and toggling `hidden` keeps the skeleton built-once.
    attrs: { type: 'button', 'aria-label': 'Clear search', hidden: true },
    on: { click: () => clearSearch() },
  });

  const box = el('div', { className: 'sm-search' });
  append(
    box,
    el('label', {
      className: 'sm-visually-hidden',
      text: 'Search artists, albums, songs, genres',
      attrs: { for: 'sm-search' },
    }),
    el('span', { className: 'sm-search__icon', text: '⌕', attrs: { 'aria-hidden': 'true' } }),
    input,
    clearButton,
  );

  /* ── the genre filter chips (AC 11.9, mock L353-359) ────────────────── */

  /** §3: "the first 8 non-Unknown genres". */
  const chipGenres = genres.filter((genre) => genre.name !== 'Unknown').slice(0, MAX_GENRE_CHIPS);

  /** @type {import('@streamymusic/shared').CatalogRef|null} */
  let activeGenre = null;

  const chipRow = el('div', {
    className: 'sm-chip-row',
    attrs: { role: 'group', 'aria-label': 'Genre filter', hidden: true },
  });
  /** @type {Map<string, HTMLElement>} chip element by genre id */
  const chipsById = new Map();
  for (const genre of chipGenres) {
    const chip = Chip({
      label: genre.name,
      pressed: false,
      testId: genreFilterChip(genre.name),
      onClick: () => toggleGenre(genre),
    });
    chipsById.set(genre.id, chip);
    append(chipRow, chip);
  }

  /** `aria-pressed` is the single source of the active fill (Chip.js). */
  function paintChips() {
    for (const [genreId, chip] of chipsById) {
      chip.setAttribute('aria-pressed', String(activeGenre !== null && activeGenre.id === genreId));
    }
  }

  const resultsHost = el('div');
  append(section, heading, box, chipRow, resultsHost);

  /* ── state ──────────────────────────────────────────────────────────── */

  let disposed = false;
  /** The pending debounce timer — CLEARED AND RE-ARMED on every keystroke. */
  /** @type {ReturnType<typeof setTimeout>|null} */
  let timer = null;
  /** See the module header: invalidates in-flight settles on clear/below-min. */
  let epoch = 0;
  /** The last trimmed term actually SEARCHED — what Retry and labels use. */
  let term = '';

  /* ── result-host states ─────────────────────────────────────────────── */

  /**
   * Feature #7 — the recent-searches block, shown on the idle screen when the
   * box is EMPTY (not mid-type). Each term re-runs the search on tap; a Clear
   * wipes the list. Returns null when there is nothing to show.
   *
   * @returns {HTMLElement|null}
   */
  function RecentSearches() {
    if (input.value.trim() !== '') return null;
    const terms = history.list();
    if (terms.length === 0) return null;

    const wrap = el('div', { className: 'sm-recent' });
    const head = el('div', { className: 'sm-storage-head' });
    append(
      head,
      el('span', { className: 'sm-hint', text: 'Recent searches' }),
      el('button', {
        className: 'sm-recent__clear',
        text: 'Clear',
        attrs: { type: 'button' },
        on: {
          click: () => {
            history.clear();
            showIdle();
          },
        },
      }),
    );
    append(wrap, head);

    const rows = el('div', { className: 'sm-stack-6' });
    for (const t of terms) {
      append(
        rows,
        el('button', {
          className: 'sm-recent__item',
          text: `⌕  ${t}`, // `text` sets textContent — the term is never markup
          attrs: { type: 'button' },
          on: {
            click: () => {
              input.value = t;
              syncBoxChrome();
              void runSearch(t);
            },
          },
        }),
      );
    }
    append(wrap, rows);
    return wrap;
  }

  function showIdle() {
    const recent = RecentSearches();
    resultsHost.replaceChildren(
      ...(recent === null ? [] : [recent]),
      el('p', { className: 'sm-hint', text: SEARCH_IDLE_HINT }),
    );
  }

  function showTooLong() {
    resultsHost.replaceChildren(el('p', { className: 'sm-hint', text: SEARCH_TOO_LONG_HINT }));
  }

  /** AC 11.7 — mock L361, the 18 px pink ring + "Digging…". */
  function showInFlight() {
    const row = el('div', { className: 'sm-spinner-row', attrs: { role: 'status' } });
    append(row, Spinner({ size: 'search', announce: false }), 'Digging…');
    resultsHost.replaceChildren(row);
  }

  function showError() {
    resultsHost.replaceChildren(
      ErrorBlock({
        message: SEARCH_ERROR_MESSAGE,
        onRetry: () => {
          void runSearch(term);
        },
        ...(announce === undefined ? {} : { announce }),
      }),
    );
  }

  function showNoResults() {
    resultsHost.replaceChildren(
      EmptyBlock({
        headline: SEARCH_NO_RESULTS_HEADLINE,
        message: searchNoResultsMessage(term, activeGenre === null ? null : activeGenre.name),
        ...(announce === undefined ? {} : { announce }),
      }),
    );
  }

  /* ── result rows ────────────────────────────────────────────────────── */

  /**
   * @param {import('@streamymusic/shared').CatalogRef} artist
   * @returns {HTMLElement}
   */
  function ArtistResultRow(artist) {
    const row = el('a', {
      className: 'sm-row sm-row--gap10',
      testId: artistRowLink(artist.id),
      attrs: { href: `#/artists/${encodeURIComponent(artist.id)}` },
    });
    append(
      row,
      artistThumb(artist, 'sm-thumb sm-thumb--circle sm-thumb--circle-32'),
      el('span', { className: 'sm-line-name', text: artist.name }),
      el('span', { className: 'sm-kind-label', text: 'ARTIST' }),
    );
    return row;
  }

  /**
   * @param {import('@streamymusic/shared').AlbumCard} album
   * @returns {HTMLElement}
   */
  function AlbumResultRow(album) {
    // The AC 3.9 retained exception: a gone ALBUM stays in search results and
    // carries its marker (kind:'album' never trips the filtered-surface scope).
    const marker = availabilityState({
      available: album.available,
      offline,
      // Album cards take the ALBUM roll-up; song rows take the per-track set.
      // Mixing them is the AC 13.5 defeat AvailabilityBadge's header describes.
      downloaded: downloadedAlbumIds.has(album.id),
      kind: 'album',
      surface: 'search',
      onScopeViolation: (message) => warn(message, { surface: 'search' }),
    });

    const row = el('a', {
      className: 'sm-row sm-row--gap10',
      testId: albumCardLink(album.id),
      attrs: { href: `#/albums/${encodeURIComponent(album.id)}` },
      ...(marker === null ? {} : { styleProps: { opacity: String(availabilityOpacity(marker)) } }),
    });

    const text = el('span', { className: 'sm-flex-fill' });
    append(
      text,
      el('span', { className: 'sm-line-primary', text: album.title }),
      el('span', { className: 'sm-line-secondary', text: albumResultSubtitle(album) }),
      marker === null ? null : AvailabilityBadge({ state: marker }),
    );

    append(
      row,
      albumThumb(album, 'sm-thumb sm-thumb--36'),
      text,
      el('span', { className: 'sm-kind-label', text: 'ALBUM' }),
    );
    return row;
  }

  /**
   * A song result is a BUTTON, not a link — tapping it PLAYS (AC 11.1). The
   * element choice is an accessibility contract: a screen reader announces it
   * as an action, and Enter/Space both activate it.
   *
   * @param {import('@streamymusic/shared').SearchTrack} track
   * @returns {HTMLElement}
   */
  function SongResultRow(track) {
    // `surface:'search'` — a FILTERED surface: an `available:false` track here
    // is a server contract violation, reported to diagnostics (never a badge).
    const marker = availabilityState({
      available: track.available,
      formatSupported: track.formatSupported,
      offline,
      downloaded: downloadedTrackIds.has(track.id),
      kind: 'track',
      surface: 'search',
      onScopeViolation: (message) => warn(message, { surface: 'search', trackId: track.id }),
    });

    const row = el('button', {
      className: 'sm-row sm-row--gap10 sm-row--button',
      testId: playTrackButton(track.title),
      attrs: { type: 'button', 'aria-label': `Play ${track.title}` },
      on: {
        click: () => {
          if (marker !== null) {
            // Never start a stream that cannot work — §10.6 per-state copy.
            onBlocked(AVAILABILITY_COPY[marker].banner, {
              state: marker,
              trackId: track.id,
            });
            return;
          }
          onPlayTrack(track);
        },
      },
    });

    const text = el('span', { className: 'sm-flex-fill' });
    append(
      text,
      el('span', { className: 'sm-line-primary', text: track.title }),
      el('span', { className: 'sm-line-secondary', text: songResultSubtitle(track) }),
      marker === null ? null : AvailabilityBadge({ state: marker }),
    );

    append(
      row,
      el('span', { className: 'sm-row__play-glyph', text: '▶', attrs: { 'aria-hidden': 'true' } }),
      text,
      el('span', { className: 'sm-line-trailing', text: formatSeconds(track.durationS) }),
    );
    return row;
  }

  /**
   * @param {import('@streamymusic/shared').CatalogRef} genre
   * @returns {HTMLElement}
   */
  function GenreResultRow(genre) {
    const row = el('a', {
      className: 'sm-row sm-row--genre',
      testId: genreRowLink(genre.id),
      attrs: { href: `#/genres/${encodeURIComponent(genre.id)}` },
    });
    append(
      row,
      el('span', { className: 'sm-line-name sm-line-name--plain', text: genre.name }),
      // NOT the mock's "N albums": the search DTO's genre items carry no album
      // count (see the module header), so the honest trailing text is the kind
      // label the other row shapes already use.
      el('span', { className: 'sm-kind-label', text: 'GENRE' }),
    );
    return row;
  }

  /* ── painting a fresh response ──────────────────────────────────────── */

  /**
   * @template T
   * @param {{ key: string, label: string, accent: string }} meta
   * @param {{ items: T[], count: number }} group
   * @param {(item: T) => HTMLElement} renderRow
   * @returns {HTMLElement}
   */
  function GroupSection(meta, group, renderRow) {
    const wrap = el('div');
    const rows = el('div', { className: 'sm-stack-6' });
    for (const item of group.items) append(rows, renderRow(item));
    append(
      wrap,
      // `count` is the TOTAL across all pages (AC 11.1), formatted the
      // contract's way — `toLocaleString()`.
      el('h2', {
        className: ['sm-section-title', meta.accent],
        text: `${meta.label} (${group.count.toLocaleString()})`,
      }),
      rows,
    );
    return wrap;
  }

  /**
   * @param {import('@streamymusic/shared').SearchResponse} data
   * @returns {void}
   */
  function paintResults(data) {
    const total =
      (data.artists?.count ?? 0) +
      (data.albums?.count ?? 0) +
      (data.songs?.count ?? 0) +
      (data.genres?.count ?? 0);

    // W17 — on a debounced search response return, and ONLY the two §11
    // properties. `query_length`, never the query: the term itself is PII by
    // policy and must not cross the telemetry wire.
    (telemetry ?? getTelemetryEmitter()).emit('search_performed', {
      query_length: term.length,
      result_count: total,
    });

    if (total === 0) {
      showNoResults();
      return;
    }

    const grid = el('div', { className: 'sm-search-results', attrs: { 'aria-live': 'polite' } });
    for (const meta of GROUP_ORDER) {
      // Optional-and-meaningful: an ABSENT group (genres under an active chip,
      // §10.6) renders no section at all — this loop never invents one.
      const group = data[meta.key];
      if (group === undefined || group.items.length === 0) continue;
      if (meta.key === 'artists') {
        append(grid, GroupSection(meta, group, ArtistResultRow));
      } else if (meta.key === 'albums') {
        append(grid, GroupSection(meta, group, AlbumResultRow));
      } else if (meta.key === 'songs') {
        append(grid, GroupSection(meta, group, SongResultRow));
      } else {
        append(grid, GroupSection(meta, group, GenreResultRow));
      }
    }
    resultsHost.replaceChildren(grid);

    // The count announcement (AC 11.5 / §9): the shell's PERSISTENT polite
    // region, because a freshly inserted aria-live container is not reliably
    // read (see StateBlocks.js#ErrorBlock).
    if (announce !== undefined) announce(`${total.toLocaleString()} results`);
  }

  /* ── issuing a search ───────────────────────────────────────────────── */

  /**
   * @param {string} nextTerm trimmed, length already validated
   * @returns {Promise<void>}
   */
  async function runSearch(nextTerm) {
    const own = (epoch += 1);
    term = nextTerm;
    // Record the live query AND the active genre filter in the URL so tapping a
    // result and pressing Back returns to THESE results with the same filter,
    // not an empty box (#back). No-op in isolation; the composition root wires
    // it to a `replaceState`, so it never spams history (one write per settled
    // search, not per keystroke).
    onQueryChange?.(nextTerm, activeGenre === null ? null : activeGenre.id);
    showInFlight();

    try {
      const outcome = await api.search(nextTerm, {
        limit: SEARCH_GROUP_LIMIT,
        ...(activeGenre === null ? {} : { genreId: activeGenre.id }),
      });
      if (disposed || own !== epoch) return;
      // AC 11.8 — latest-query-wins. The api-client's shared guard sequences
      // every search call and reports all but the newest as stale; discarding
      // here is the WHOLE mechanism (see the module header).
      if (outcome.stale) return;
      paintResults(outcome.value);
      // Feature #7 — remember the term the user actually searched (most-recent
      // first, de-duped) so the idle screen can offer one-tap re-runs.
      history.add(nextTerm);
    } catch (error) {
      if (disposed || own !== epoch) return;
      const code = /** @type {{ code?: string }} */ (error).code;
      if (code === ERROR_CODES.NOT_FOUND && activeGenre !== null) {
        // An unknown `genreId` — the api-client contract says drop the chip,
        // and the same term re-runs unfiltered rather than dead-ending.
        activeGenre = null;
        paintChips();
        void runSearch(nextTerm);
        return;
      }
      showError();
    }
  }

  /* ── input wiring ───────────────────────────────────────────────────── */

  /** The ✕ shows only while a query is present; chips only from 2 chars. */
  function syncBoxChrome() {
    if (input.value === '') clearButton.setAttribute('hidden', '');
    else clearButton.removeAttribute('hidden');

    if (chipGenres.length > 0 && input.value.trim().length >= SEARCH_QUERY_MIN_CHARS) {
      chipRow.removeAttribute('hidden');
    } else {
      chipRow.setAttribute('hidden', '');
    }
  }

  function onInput() {
    syncBoxChrome();
    // THE RE-ARM. Clearing before re-arming is what makes this a debounce and
    // not a throttle: the countdown restarts from the LAST keystroke, so a
    // burst issues exactly one request — for the final term.
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }

    const trimmed = input.value.trim();
    if (trimmed.length < SEARCH_QUERY_MIN_CHARS) {
      // AC 11.4 — below the minimum NOTHING fires (a 1-char `q` is a 422 by
      // contract, and "No results for 'a'" would describe a search never
      // run). The pre-search view is restored and any in-flight settle is
      // orphaned via the epoch bump.
      epoch += 1;
      showIdle();
      return;
    }
    if (trimmed.length > SEARCH_QUERY_MAX_CHARS) {
      epoch += 1;
      showTooLong();
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      void runSearch(trimmed);
    }, debounceMs);
  }

  /** AC 11.2 — clearing restores the pre-search view and cancels everything. */
  function clearSearch() {
    input.value = '';
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    epoch += 1;
    // The mock resets the genre filter on clear too (L1045 `sqGenre:''`).
    activeGenre = null;
    paintChips();
    syncBoxChrome();
    // Drop the query (and filter) from the URL to match the now-empty box (#back).
    onQueryChange?.('', null);
    showIdle();
    // The ✕ just hid itself; keyboard focus must not fall to <body>.
    input.focus();
  }

  /**
   * @param {import('@streamymusic/shared').CatalogRef} genre
   * @returns {void}
   */
  function toggleGenre(genre) {
    activeGenre = activeGenre !== null && activeGenre.id === genre.id ? null : genre;
    paintChips();

    const trimmed = input.value.trim();
    if (trimmed.length < SEARCH_QUERY_MIN_CHARS || trimmed.length > SEARCH_QUERY_MAX_CHARS) return;
    // A tap is a decision, not typing — no debounce, and any pending timer
    // would carry the now-wrong filter, so it is superseded outright.
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    void runSearch(trimmed);
  }

  /* ── initial state ──────────────────────────────────────────────────── */

  // Restore the genre filter from the URL (#back) BEFORE the seeded query runs,
  // so the immediate search below carries it. Only a chip we actually render can
  // be reinstated; an unknown id is ignored (the results come back unfiltered).
  if (initialGenreId !== undefined) {
    const seeded = chipGenres.find((genre) => genre.id === initialGenreId);
    if (seeded !== undefined) {
      activeGenre = seeded;
      paintChips();
    }
  }

  if (initialQuery !== '') {
    input.value = initialQuery;
    syncBoxChrome();
    const trimmed = initialQuery.trim();
    if (trimmed.length >= SEARCH_QUERY_MIN_CHARS && trimmed.length <= SEARCH_QUERY_MAX_CHARS) {
      // A seeded `?q=` is a navigation, not a keystroke: search immediately.
      void runSearch(trimmed);
    } else if (trimmed.length > SEARCH_QUERY_MAX_CHARS) {
      showTooLong();
    } else {
      showIdle();
    }
  } else {
    showIdle();
  }

  return {
    element: section,
    dispose() {
      // Orphans every unsettled response: the next settle sees `disposed` and
      // writes nothing into a section the shell has already replaced. The
      // transport itself is the api-client guard's to abort — this view never
      // held a controller (see the module header).
      disposed = true;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
